// eslint-disable-next-line camelcase
import murmurhash3_32_gc from './murmurHash.js';
import {logError} from '../fingerprint.js';
import {noopLap} from './timings.js';

/***
 * @param {WebGLRenderingContext} ctx Контекст WebGL.
 * @returns {IWebGLAttributes} Возвращает объект с фактическими параметрами контекста
 */
function getWebGlAttributes(ctx) {
    const attributes = ctx.getContextAttributes();
    const worthlessAttributes = ['alpha', 'depth', 'failIfMajorPerformanceCaveat', 'stencil', 'powerPreference', 'premultipliedAlpha', 'preserveDrawingBuffer'];
    const webGlAttributes = {};

    Object.keys(attributes).forEach(key => {
        if (!worthlessAttributes.includes(key)) {
            webGlAttributes[`WEBGL_${key}`] = attributes[key];
        }
    });

    return webGlAttributes;
}

/**
 * Получает значения параметров из списка ниже
 *
 * @param {WebGLRenderingContext} ctx Контекст WebGL.
 * @returns {IWebGLParams} Объект с различными параметрами WebGLRenderingContext.
 */
function getWebGlParams(ctx) {
    const paramsWebGl1 = [
        'ALIASED_LINE_WIDTH_RANGE',
        'ALIASED_POINT_SIZE_RANGE',
        'ARRAY_BUFFER_BINDING',
        'CURRENT_PROGRAM',
        'FRONT_FACE',
        'LINE_WIDTH',
        'MAX_COMBINED_TEXTURE_IMAGE_UNITS',
        'MAX_CUBE_MAP_TEXTURE_SIZE',
        'MAX_FRAGMENT_UNIFORM_VECTORS',
        'MAX_RENDERBUFFER_SIZE',
        'MAX_TEXTURE_IMAGE_UNITS',
        'MAX_TEXTURE_SIZE',
        'MAX_VARYING_VECTORS',
        'MAX_VERTEX_ATTRIBS',
        'MAX_VERTEX_TEXTURE_IMAGE_UNITS',
        'MAX_VERTEX_UNIFORM_VECTORS',
        'MAX_VIEWPORT_DIMS',
        'SAMPLE_BUFFERS',
        'SAMPLES',
        'SHADING_LANGUAGE_VERSION',
        'STENCIL_BACK_VALUE_MASK',
        'STENCIL_BACK_WRITEMASK',
        'STENCIL_VALUE_MASK',
        'STENCIL_WRITEMASK',
        'SUBPIXEL_BITS',
        'VERSION',
    ];
    const paramsWebGl2 = [
        'DRAW_BUFFER0',
        'FRAGMENT_SHADER_DERIVATIVE_HINT',
        'MAX_3D_TEXTURE_SIZE',
        'MAX_ARRAY_TEXTURE_LAYERS',
        'MAX_CLIENT_WAIT_TIMEOUT_WEBGL',
        'MAX_COLOR_ATTACHMENTS',
        'MAX_COMBINED_FRAGMENT_UNIFORM_COMPONENTS',
        'MAX_COMBINED_UNIFORM_BLOCKS',
        'MAX_COMBINED_VERTEX_UNIFORM_COMPONENTS',
        'MAX_DRAW_BUFFERS',
        'MAX_ELEMENT_INDEX',
        'MAX_ELEMENTS_INDICES',
        'MAX_ELEMENTS_VERTICES',
        'MAX_FRAGMENT_INPUT_COMPONENTS',
        'MAX_FRAGMENT_UNIFORM_BLOCKS',
        'MAX_FRAGMENT_UNIFORM_COMPONENTS',
        'MAX_PROGRAM_TEXEL_OFFSET',
        'MAX_SAMPLES',
        'MAX_SERVER_WAIT_TIMEOUT',
        'MAX_TEXTURE_LOD_BIAS',
        'MAX_TRANSFORM_FEEDBACK_INTERLEAVED_COMPONENTS',
        'MAX_TRANSFORM_FEEDBACK_SEPARATE_ATTRIBS',
        'MAX_TRANSFORM_FEEDBACK_SEPARATE_COMPONENTS',
        'MAX_UNIFORM_BLOCK_SIZE',
        'MAX_UNIFORM_BUFFER_BINDINGS',
        'MAX_VARYING_COMPONENTS',
        'MAX_VERTEX_OUTPUT_COMPONENTS',
        'MAX_VERTEX_UNIFORM_BLOCKS',
        'MAX_VERTEX_UNIFORM_COMPONENTS',
        'MIN_PROGRAM_TEXEL_OFFSET',
        'PACK_ROW_LENGTH',
        'PACK_SKIP_PIXELS',
        'PACK_SKIP_ROWS',
        'RASTERIZER_DISCARD',
        'READ_BUFFER',
        'TRANSFORM_FEEDBACK_ACTIVE',
        'TRANSFORM_FEEDBACK_PAUSED',
        'UNIFORM_BUFFER_OFFSET_ALIGNMENT',
        'UNPACK_IMAGE_HEIGHT',
        'UNPACK_ROW_LENGTH',
        'UNPACK_SKIP_IMAGES',
        'UNPACK_SKIP_PIXELS',
        'UNPACK_SKIP_ROWS',
    ];

    return [...paramsWebGl1, ...paramsWebGl2].reduce((acc, param) => {
        if (ctx[param] !== undefined) {
            const value = ctx.getParameter(ctx[param]);
            acc[`WEBGL_${param}`] = (value instanceof Float32Array || value instanceof Int32Array) ? [...value] : value;
        } else {
            acc[`WEBGL_${param}`] = null;
        }
        return acc;
    }, {});
}

/**
 * Параметры всех доступных расширений WebGL.
 *
 * @param {WebGLRenderingContext} ctx Контекст WebGL.
 * @returns {IWebGLExtensionsParams} Объект с параметрами всех доступных расширений.
 */
function getExtensionsParams(ctx) {
    /** @type {IWebGLExtensionsParams} */
    const extensionsParams = {};

    const supportedExtensions = ctx.getSupportedExtensions();
    if (!supportedExtensions) {
        return extensionsParams;
    }

    const worthlessExtensions = ['WEBGL_debug_renderer_info'];
    const filteredExtensions = supportedExtensions.filter(ext => !worthlessExtensions.includes(ext));

    filteredExtensions.forEach(extensionName => {
        /** @type {IWebGLExtension | null} */
        const extension = ctx.getExtension(extensionName);
        if (!extension) {
            return;
        }

        let paramStr = '';

        for (const paramName in extension) {
            if (typeof extension[paramName] !== 'function') {
                paramStr = paramStr + `${paramName}: ${extension[paramName]}, `;
            }
        }

        paramStr = paramStr.slice(0, -2);

        if (paramStr !== '') {
            extensionsParams[`WEBGL_${extensionName}`] = paramStr;
        }
    });

    return extensionsParams;
}

/**
 * @param {WebGLRenderingContext} ctx Контекст WebGL.
 * @returns {IWebGLShaders} возвращает объект, описывающий диапазон и точность для разных типов шейдеров
 */
function getShaders(ctx) {
    const shaders = {};
    const shaderTypes = ['FRAGMENT_SHADER', 'VERTEX_SHADER'];
    const dataTypes = ['LOW_FLOAT', 'MEDIUM_FLOAT', 'LOW_INT', 'MEDIUM_INT', 'HIGH_INT'];

    shaderTypes.forEach(shaderType => {
        dataTypes.forEach(dataType => {
            const precision = ctx.getShaderPrecisionFormat(ctx[shaderType], ctx[dataType]);
            if (precision) {
                shaders[`WEBGL_${shaderType}-${dataType}`] = `rangeMin: ${precision.rangeMin}, rangeMax: ${precision.rangeMax}, precision: ${precision.precision}`;
            } else {
                shaders[`WEBGL_${shaderType}-${dataType}`] = null; // eсли precision равен null
            }
        });
    });

    return shaders;
}

/**
 *
 * @param {WebGLRenderingContext} ctx
 * @param {int} width
 * @param {int} height
 * @returns {string}
 */
function getCanvasPixelDataHash(ctx, width, height) {
    const pixels = new Uint8Array(width * height * 4);

    ctx.readPixels(0, 0, width, height, ctx.RGBA, ctx.UNSIGNED_BYTE, pixels);

    // Preserve the legacy hash input: braces and decimal bytes without separators.
    return '{' + pixels.join('') + '}';
}

/**
 * Производитель и модель GPU: всё, что из WebGL читает бот-детектор.
 * @param {WebGLRenderingContext} ctx
 */
function getWebGlRenderer(ctx) {
    const vendor = ctx.getParameter(ctx.VENDOR);
    const renderer = ctx.getParameter(ctx.RENDERER);
    const debugInfo = ctx.getExtension('WEBGL_debug_renderer_info');
    const unmaskedVendor = ctx.getParameter(debugInfo?.UNMASKED_VENDOR_WEBGL) || vendor;
    const unmaskedRenderer = ctx.getParameter(debugInfo?.UNMASKED_RENDERER_WEBGL) || renderer;

    return {
        WEBGL_VENDOR: vendor,
        WEBGL_UNMASKED_VENDOR: unmaskedVendor,
        WEBGL_RENDERER: renderer,
        WEBGL_UNMASKED_RENDERER: unmaskedRenderer,
    };
}

/**
 * @param {WebGLRenderingContext} ctx
 * @param {function(string)} lap Отметка этапа для замеров (timings.js).
 * @returns {IWebGLInfo}
 */
function getWebGlInfo(ctx, lap = noopLap) {
    const rendererInfo = getWebGlRenderer(ctx);
    const extensions = ctx.getSupportedExtensions();
    lap('renderer');
    // Части вычисляются в прежнем порядке вызовов; порядок полей в объекте тоже прежний.
    const attributes = getWebGlAttributes(ctx);
    const params = getWebGlParams(ctx);
    lap('params');
    const extensionsParams = getExtensionsParams(ctx);
    lap('extensions');
    const shaders = getShaders(ctx);
    lap('shaders');

    return {
        ...rendererInfo,
        WEBGL_EXTENSIONS: extensions,
        ...attributes,
        ...params,
        ...extensionsParams,
        ...shaders,
    };
}
/**
 * Возвращает список параметров webGL и хэш канваса, либо null
 * @param {IFingerprintParams} params
 * @param {function(string)} lap Отметка этапа для замеров (timings.js).
 * @param {boolean} rendererOnly Только производитель и модель GPU, без отрисовки и параметров
 *     (облегчённый режим iOS, см. fingerprint.js).
 * @returns {Promise<IWebGLData>}
 */
async function collectWebGl(params, lap = noopLap, rendererOnly = false) {
    const width = 256;
    const height = 128;
    // Пытаемся получить самый продвинутый контекст, иначе откатываемся к WebGL1.
    const createCanvasContext = params.createWebglContext
        ? params.createWebglContext
        : () => {
            // Canvas не вставляем в страницу: значения те же (readPixels читает буфер отрисовки),
            // а сбор не зависит от наличия body и не оставляет на странице живой WebGL-контекст.
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            return canvas.getContext('webgl2')
                || canvas.getContext('experimental-webgl2')
                || canvas.getContext('webgl')
                || canvas.getContext('experimental-webgl')
                || canvas.getContext('moz-webgl');
        };
    const ctx = createCanvasContext();
    lap('context');
    if (!ctx) {
        return {
            WEBGL_HASH: 0, // Результат хэширования пустой строки
        };
    }

    try {
        if (rendererOnly) {
            const rendererInfo = getWebGlRenderer(ctx);
            lap('renderer');
            return rendererInfo;
        }

        // Минимальные шейдеры для детерминированного рисунка.
        const f = 'attribute vec2 attrVertex;varying vec2 varyinTexCoordinate;uniform vec2 uniformOffset;void main(){varyinTexCoordinate=attrVertex+uniformOffset;gl_Position=vec4(attrVertex,0,1);}';
        const g = 'precision mediump float;varying vec2 varyinTexCoordinate;void main() {gl_FragColor=vec4(varyinTexCoordinate,0,1);}';
        const h = ctx.createBuffer();

        ctx.bindBuffer(ctx.ARRAY_BUFFER, h);

        const i = new Float32Array([-.2, -.9, 0, .4, -.26, 0, 0, .7321, 0]);

        ctx.bufferData(ctx.ARRAY_BUFFER, i, ctx.STATIC_DRAW);
        h.itemSize = 3;
        h.numItems = 3;

        const j = ctx.createProgram();
        const k = ctx.createShader(ctx.VERTEX_SHADER);

        ctx.shaderSource(k, f);
        ctx.compileShader(k);

        const l = ctx.createShader(ctx.FRAGMENT_SHADER);

        ctx.shaderSource(l, g);
        ctx.compileShader(l);
        ctx.attachShader(j, k);
        ctx.attachShader(j, l);
        ctx.linkProgram(j);
        ctx.useProgram(j);

        j.vertexPosAttrib = ctx.getAttribLocation(j, 'attrVertex');
        j.offsetUniform = ctx.getUniformLocation(j, 'uniformOffset');

        ctx.enableVertexAttribArray(j.vertexPosArray);
        ctx.vertexAttribPointer(j.vertexPosAttrib, h.itemSize, ctx.FLOAT, !1, 0, 0);
        ctx.uniform2f(j.offsetUniform, 1, 1);
        ctx.drawArrays(ctx.TRIANGLE_STRIP, 0, h.numItems);
        lap('draw');

        const webGlHash = murmurhash3_32_gc(getCanvasPixelDataHash(ctx, width, height));
        lap('hash');

        return {
            WEBGL_HASH: webGlHash,
            ...getWebGlInfo(ctx, lap),
        };
    } catch (e) {
        logError(e);
        return {
            WEBGL_HASH: 0, // Результат хэширования пустой строки
        };
    }
}

export default collectWebGl;
